import React, { useMemo } from 'react'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { ShareOutlineIcon } from '@/atoms/Icons/ShareOutlineIcon'
import { CaptionSM, LabelSM, LabelXS, ParagraphMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { CatalogTitle, findHydraExternalId, hasHydraIdAndSlug } from '@/services/ContentCatalog'
import { Episode } from '@/services/ProjectsService'
import { RecommendedUpNextContent } from '@/services/RecommendationService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { TranslateFunction } from '@/utils/translate/translate-client'
import { EndOfAssetContentRecommendation } from './EndOfAssetContentRecommendation'

interface EndOfAssetLayoutProps {
  logoUrl: string
  isGuildMember?: boolean
  t: TranslateFunction
  projectName?: string
  handleShareButtonClick: () => void
  recommendedUpNext: RecommendedUpNextContent
  handleClose?: () => void
  loading?: boolean
  episode?: Episode
  handleJoinGuildClick: () => void
  handleRecommendationClick: (slug?: string | null) => void
  handleDiscoverMoreClick?: () => void
  projectType?: string
  watchableTitle?: CatalogTitle | null
}

export const EndOfAssetLayout: React.FC<EndOfAssetLayoutProps> = ({
  logoUrl,
  isGuildMember = false,
  t,
  projectName,
  handleShareButtonClick,
  recommendedUpNext,
  handleClose,
  loading = false,
  episode,
  handleJoinGuildClick,
  handleRecommendationClick,
  handleDiscoverMoreClick,
  projectType,
  watchableTitle,
}) => {
  const episodeType = useMemo(() => {
    return projectType === 'movie'
      ? t('movie', 'Movie')
      : projectType === 'special'
      ? t('special', 'Special')
      : t('series', 'Series')
  }, [projectType, t])

  const canSeeShareButton =
    watchableTitle &&
    hasHydraIdAndSlug(watchableTitle) &&
    isGuildMember &&
    episode?.id &&
    findHydraExternalId(watchableTitle) === episode.id

  return (
    <>
      <img
        alt="Project Logo Image"
        className="ml-4 w-auto !max-w-[167px] md:!max-w-[222px] lg:ml-0 lg:max-h-[72px] lg:!max-w-[380px]"
        src={getCloudinaryImageUrl({
          path: logoUrl,
          width: 380,
          format: 'webp',
          transforms: 'e_trim,q_auto:best',
        })}
      />
      <When condition={episode?.projectSlug === slugs.homestead}>
        <ParagraphMD className="2xl:photon-paragraph-lg mx-4 mt-2 lg:mx-0" weight="bold">
          {t('watchMovieStreamSeries', 'Watch the Movie - Stream the Series')}
        </ParagraphMD>
      </When>
      <CaptionSM
        className="2xl:photon-caption-md mx-4 mt-6 max-w-full lg:mx-0 lg:max-w-[535px] 2xl:max-w-[577px]"
        color="core-gray-400"
      >
        {isGuildMember
          ? t(
              'thanksForWatchingProjectGuildHelpsAvailableStreaming',
              'Your membership in the Angel Guild helps make {{ projectName }} available for streaming and lets you share any {{ episodeType }} on Angel for free viewing!',
              {
                projectName,
                episodeType,
              },
            )
          : t(
              'thanksForWatchingProjectNonGuildLight',
              'You were able to watch for free thanks to an Angel Guild member! Join the Guild to help create more light amplifying shows!',
            )}
      </CaptionSM>
      <When condition={canSeeShareButton}>
        <Button
          onClick={handleShareButtonClick}
          variant="white"
          className="mx-4 mt-4 flex h-12 max-h-8 flex-row items-center justify-center gap-1 whitespace-nowrap px-4 py-[7px] lg:mx-0 lg:mt-6 lg:max-h-12 lg:px-[18px]"
        >
          <div className="lg:hidden">
            <ShareOutlineIcon size={16} className="-mt-0.5" color={'black'} />
          </div>
          <div className="hidden lg:flex">
            <ShareOutlineIcon size={24} className="-mt-0.5" color={'black'} />
          </div>
          <LabelXS className="lg:photon-label-md 2xl:photon-label-lg flex h-full items-center justify-center">
            {t('shareFreeAccess', 'Share Free Access')}
          </LabelXS>
        </Button>
      </When>
      <When condition={!isGuildMember}>
        <LinkButton
          onClick={handleJoinGuildClick}
          href={`${paths.guild.join}/${projectName}`}
          variant="white"
          className="mx-4 mt-4 flex h-12 max-h-8 w-fit flex-row items-center justify-center gap-1 whitespace-nowrap px-4 py-[7px] lg:mx-0 lg:mt-6 lg:max-h-12 lg:px-[18px]"
        >
          <div className="lg:hidden">
            <GuildIconMono size={16} className="-mt-0.5" color={'black'} />
          </div>
          <div className="hidden lg:flex">
            <GuildIconMono size={24} className="-mt-0.5" color={'black'} />
          </div>
          <LabelXS className="lg:photon-label-md 2xl:photon-label-lg flex h-full items-center justify-center">
            {t('joinTheGuild', 'Join the Guild')}
          </LabelXS>
        </LinkButton>
      </When>
      <EndOfAssetContentRecommendation
        t={t}
        projectName={projectName}
        recommendedUpNext={recommendedUpNext}
        loading={loading}
        handleRecommendationClick={handleRecommendationClick}
        handleDiscoverMoreClick={handleDiscoverMoreClick}
      />
      <div className="-ml-4 flex w-full flex-row items-center justify-center gap-4 lg:hidden">
        <Button
          onClick={handleShareButtonClick}
          variant="white"
          className="mt-4 flex h-12 max-h-10 min-w-[160px] flex-row items-center justify-center gap-1 whitespace-nowrap px-4 py-[7px] lg:px-[18px]"
        >
          <LabelSM>{t('searchAllShows', 'Search All Shows')}</LabelSM>
        </Button>
        <Button
          onClick={handleClose}
          variant="core-gray-800"
          className="mt-4 flex h-12 max-h-10 min-w-[160px] flex-row items-center justify-center gap-1 whitespace-nowrap px-4 py-[7px] lg:px-[18px]"
        >
          <LabelSM>{t('close', 'Close')}</LabelSM>
        </Button>
      </div>
    </>
  )
}
